import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid2,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccordionUpIcon from "assets/svg/AccordionUpIcon";
import HeadSetMic from "assets/svg/HeadSetMic";
import HelpSupportIcon from "assets/svg/HelpSupportIcon";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_FAQS } from "reduxStore/actions/authActionType";
import { SupportFAQData, SupportTicketRequest } from "reduxStore/dataTypes";
import { RootState } from "reduxStore/store";
import theme, { responsiveFontSize } from "themes/Theme";
import { commonStyle } from "../common.styles";
import { toast } from "react-toastify";
import { supportTicketRequest } from "services/apiService";
import AccordionDownIcon from "assets/svg/AccordionDownIcon";

const SupportHelpPage = () => {
  const [expanded, setExpanded] = useState("panel1");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { supportFAQ, loading: faqLoading } = useSelector(
    (state: RootState) => state.faq
  );
  const [formValues, setFormValues] = useState({
    subject: "",
    description: "",
  });

  const [errors, setErrors] = useState({
    subject: "",
    description: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });

    // Clear the error when user starts typing
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let newErrors = { ...errors };
    let isValid = true;

    if (!formValues.subject.trim()) {
      newErrors.subject = "Subject is required";
      isValid = false;
    }

    if (!formValues.description.trim()) {
      newErrors.description = "Message is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    dispatch({ type: FETCH_FAQS });
  }, []);

  const handleChange = (panel: any) => (_event: any, newExpanded: any) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleHelpPageSubmit = async () => {
    if (validateForm()) {
      setLoading(true);
      const emailParams: SupportTicketRequest = {
        subject: formValues.subject,
        description: formValues.description,
      };

      try {
        const response = await supportTicketRequest(emailParams);
        if (response && response.status === 200) {
          toast.success(response?.data?.message);
          setFormValues({
            subject: "",
            description: "",
          });
        }
      } catch (error) {
        console.error("EmailJS Error:", error);
        toast.error("Failed to send email.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box>
      <Box sx={commonStyle.cardStyle}>
        <Box sx={commonStyle.flexSpaceBetween}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <HelpSupportIcon />
            <Typography sx={commonStyle.cardTitleText}>FAQ</Typography>
          </Stack>
        </Box>
        <Grid2 container spacing={4} sx={{ width: "100%" }} mt={3}>
          <Grid2 size={{ xs: 12, md: 8 }}>
            {supportFAQ?.data.map((item: SupportFAQData, index: number) => (
              <Accordion
                expanded={expanded === `panel${index + 1}`}
                onChange={handleChange(`panel${index + 1}`)}
                disableGutters
                key={index}
                elevation={0}
                sx={styles.accordionContainer}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  expandIcon={<AccordionDownIcon />}
                >
                  <Typography sx={styles.accordionTitleText}>
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={styles.accordionDescriptionText}>
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Box sx={styles.anyQueryContainer}>
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                <HeadSetMic />
                <Typography sx={styles.anyQueryTitle}>
                  Need help? Ask your questions here.
                </Typography>
              </Stack>

              <Stack
                direction={"column"}
                justifyContent={"flex-start"}
                sx={{ width: "100%" }}
                mt={2}
              >
                <Typography sx={commonStyle.inputLabelStyle}>
                  Enter Subject
                </Typography>
                <TextField
                  placeholder="Enter Subject"
                  size="small"
                  name="subject"
                  value={formValues.subject}
                  onChange={handleInputChange}
                  error={!!errors.subject}
                  helperText={errors.subject}
                  sx={{
                    backgroundColor: theme.palette.custom?.white,
                    borderRadius: "8px",
                    width: "100%",
                  }}
                />

                <Typography sx={commonStyle.inputLabelStyle} mt={2}>
                  Enter Message
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  name="description"
                  size="small"
                  placeholder="Enter Message"
                  value={formValues.description}
                  onChange={handleInputChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  sx={{
                    backgroundColor: theme.palette.custom?.white,
                    borderRadius: "8px",
                    width: "100%",
                  }}
                />
              </Stack>
              {loading ? (
                <CircularProgress sx={{ mt: 2 }} />
              ) : (
                <PrimaryButton
                  title="Submit"
                  onClick={handleHelpPageSubmit}
                  viewStyle={{
                    borderRadius: "999px",
                    padding: "10px 24px",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: responsiveFontSize(14),
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.2)",
                      background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    },
                    mt: 2,
                  }}
                />
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};
export default SupportHelpPage;
const styles = {
  titleText: {
    fontSize: responsiveFontSize(36),
    color: theme.palette.text.primary,
  },
  itemCardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: "0.938em",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1px",
  },
  itemCardTitle: {
    fontSize: responsiveFontSize(20),
    color: theme.palette.text.primary,
    marginTop: 1,
  },
  itemCardDesc: {
    fontSize: responsiveFontSize(12),
    color: theme.palette.text.secondary,
    wordWrap: "break-word",
    whiteSpace: "normal",
  },
  accordionTitleText: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.text.primary,
  },
  accordionDescriptionText: {
    fontSize: responsiveFontSize(12),
    color: theme.palette.text.secondary,
  },
  anyQueryTitle: {
    fontSize: responsiveFontSize(18),
    color: theme.palette.text.primary,
  },
  anyQueryContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  accordionContainer: {
    boxShadow: "none",
    border: `0.75px solid rgba(232, 232, 232, 1)`,
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "transparent",
  },
};
